<!-- Scheduler Hours, laid out like CGL's "Schedule hours" page and the Workshop Scheduler beside it. -->
<div class="hoursPage">
  <div class='schedBar'>
    <h2 class='schedHeading'>Scheduler Hours</h2>
    <button class='barBtn' routerLink='/workshop/scheduler'><i class='fas fa-stream'></i> Scheduler</button>

    <div class='schedNav'>
      <button class='barBtn navBtn' (click)='this.stepWeek(-1)' aria-label='Previous week' [disabled]='this.mode() === "usual"'><i class='fas fa-chevron-left'></i></button>
      <b class='schedTitle'>{{ this.mode() === 'usual' ? 'Usual week' : this.weekLabel() }}</b>
      <button class='barBtn navBtn' (click)='this.stepWeek(1)' aria-label='Next week' [disabled]='this.mode() === "usual"'><i class='fas fa-chevron-right'></i></button>
    </div>
    <button class='barBtn' (click)='this.thisWeek()' [disabled]='this.mode() === "usual"'>This week</button>
    <label class='barField'>
      <span>Week of</span>
      <input type='date' class='barDate' [value]='this.weekStart()' (change)='this.onWeekPick($any($event.target).value)' [disabled]='this.mode() === "usual"'>
    </label>

    <div class='seg' role='group' aria-label='What the tables edit'>
      <button class='segBtn' [class.on]='this.mode() === "week"' (click)='this.mode.set("week")'
        title='Edits change these dates only; Default puts a date back to the usual week'>This week</button>
      <button class='segBtn' [class.on]='this.mode() === "usual"' (click)='this.mode.set("usual")'
        title='Edits change every week from now on'>Usual week</button>
    </div>

    <button class='barBtn' [class.on]='this.rangeOpen()' [attr.aria-expanded]='this.rangeOpen()' (click)='this.rangeOpen.set(!this.rangeOpen())'>
      Block of days {{ this.rangeOpen() ? '▲' : '▾' }}
    </button>
  </div>

  <div class='hoursBody' [class.busy]='this.busy()'>
    <p class='hint'>
      @if (this.mode() === 'week') {
        Changes here apply to that date only – highlighted rows have one, and <b>Default</b> puts the day back to the usual week.
      } @else {
        You are editing the <b>usual week</b>: a change applies to every week (dates with their own change keep it).
      }
    </p>

    <!-- One change across many days: a shutdown, a run of overtime, someone's fortnight on nights. -->
    @if (this.rangeOpen()) {
      <section class='blk' aria-label='Change a block of days'>
        <div class='blkRow'>
          <label class='field'><span>From</span><input type='date' [value]='this.rangeFrom()' (change)='this.rangeFrom.set($any($event.target).value)'></label>
          <label class='field'><span>To</span><input type='date' [value]='this.rangeTo()' (change)='this.rangeTo.set($any($event.target).value)'></label>
          <div class='chips' role='group' aria-label='Which days of the week'>
            @for (c of this.weekdayChips; track c.day) {
              <button type='button' class='chip' [class.on]='this.isWeekdayOn(c.day)' [attr.aria-pressed]='this.isWeekdayOn(c.day)' (click)='this.toggleWeekday(c.day)'>{{ c.label }}</button>
            }
            <button type='button' class='linkBtn' (click)='this.setWeekdays([1,2,3,4,5])'>Mon–Fri</button>
            <button type='button' class='linkBtn' (click)='this.setWeekdays([0,1,2,3,4,5,6])'>All</button>
          </div>
        </div>

        <div class='blkRow'>
          <div class='seg' role='group' aria-label='Set those days to'>
            <span class='segLabel'>Set to</span>
            <button type='button' class='segBtn' [class.on]='this.rangeAction() === "closed"' (click)='this.rangeAction.set("closed")'>Closed</button>
            <button type='button' class='segBtn' [class.on]='this.rangeAction() === "open"' (click)='this.rangeAction.set("open")'>These hours</button>
            <button type='button' class='segBtn' [class.on]='this.rangeAction() === "default"' (click)='this.rangeAction.set("default")'>Usual week</button>
          </div>

          @if (this.rangeAction() === 'closed') {
            <label class='field reason'><span>Reason</span>
              <input type='text' maxlength='100' placeholder='e.g. Christmas shutdown' [value]='this.rangeReason()' (input)='this.rangeReason.set($any($event.target).value)'>
            </label>
          }
          @if (this.rangeAction() === 'open') {
            <div class='seg' role='group' aria-label='Which shift'>
              <span class='segLabel'>Shift</span>
              @for (p of this.presets; track p.key) {
                <button type='button' class='segBtn' [class.on]='this.rangeShift() === p.key' [title]='p.title' (click)='this.rangeShift.set(p.key)'>{{ p.label }}</button>
              }
              <button type='button' class='segBtn' [class.on]='this.rangeShift() === "custom"' (click)='this.rangeShift.set("custom")'>Custom</button>
            </div>
            @if (this.rangeShift() === 'custom') {
              <label class='field'><span>Start</span><input type='time' step='300' [value]='this.rangeStart()' (change)='this.rangeStart.set($any($event.target).value)'></label>
              <label class='field'><span>End</span><input type='time' step='300' [value]='this.rangeEnd()' (change)='this.rangeEnd.set($any($event.target).value)'></label>
              @if (this.rangeShifts()?.next?.length) {
                <span class='hint'>Crosses midnight – stored as {{ this.rangeStart() }}–24:00 and 00:00–{{ this.rangeEnd() }} the next day</span>
              }
            }
          }

          <div class='seg' role='group' aria-label='Whose hours change'>
            <span class='segLabel'>For</span>
            <button type='button' class='segBtn' [class.on]='this.rangeTarget() === "workshop"' (click)='this.rangeTarget.set("workshop")'>Workshop</button>
            <button type='button' class='segBtn' [class.on]='this.rangeTarget() === "fitters"' (click)='this.rangeTarget.set("fitters")'>Fitters</button>
          </div>
        </div>

        @if (this.rangeTarget() === 'fitters') {
          <div class='targets'>
            <div class='targetsHead'>
              <span>Fitters</span>
              <button type='button' class='linkBtn' (click)='this.pickAll(true)'>All</button>
              <button type='button' class='linkBtn' (click)='this.pickAll(false)'>None</button>
            </div>
            <div class='targetsGrid'>
              @for (f of this.fitters(); track f.id) {
                <label class='pick' [class.on]='this.isPicked(f.id)'>
                  <input type='checkbox' [checked]='this.isPicked(f.id)' (change)='this.togglePicked(f.id)'>
                  <span>{{ f.name }}</span>
                  <small>{{ f.ownHours ? 'own hours' : 'follows Workshop' }}</small>
                </label>
              }
            </div>
          </div>
        }

        <div class='blkFoot'>
          <span class='summary'>{{ this.rangeSummary() }}</span>
          <button class='barBtn primary' type='button' [disabled]='!this.canApplyRange()' (click)='this.applyRange()'>
            {{ this.rangeBusy() ? 'Applying…' : 'Apply' }}
          </button>
        </div>
      </section>
    }

    @if (this.error()) {
      <div class='alert alert-danger'>{{ this.error() }} <button class='btn btn-secondary btn-sm' (click)='this.load()'>Reload</button></div>
    }

    @if (this.loading()) {
      <p class='hint'>Loading…</p>
    } @else if (this.data(); as data) {
      <!-- The workshop: the week every fitter follows unless they have their own hours. -->
      <section class='acc' [class.open]='this.workshopOpen()'>
        <button class='accHead' type='button' (click)='this.workshopOpen.set(!this.workshopOpen())' [attr.aria-expanded]='this.workshopOpen()'>
          <span class='caret' [class.open]='this.workshopOpen()'>▸</span>
          <span class='accName'>Workshop</span>
          <span class='accNote'>every fitter follows this unless they have their own hours</span>
          @if (this.mode() === 'week' && this.changedCount(data.workshop.days); as n) {
            <span class='tag'>{{ n }} changed this week</span>
          }
        </button>
        @if (this.workshopOpen()) {
          <ng-container *ngTemplateOutlet='hoursTable; context: { owner: this.workshopOwner }'></ng-container>
        }
      </section>

      <!-- Fitters: each follows the workshop until given hours of their own. -->
      <section class='acc open'>
        <div class='accHead static'><span class='accName'>Fitters</span></div>
        @for (f of this.fitters(); track f.id) {
          <div class='fitter' [class.own]='f.ownHours'>
            <div class='fitterHead'>
              <button class='fitterName' type='button' [disabled]='!f.ownHours' (click)='this.toggleFitter(f)'
                [title]='f.ownHours ? "Show " + f.name + "&#39;s week" : f.name + " follows the workshop"'>
                <span class='caret' [class.open]='this.openFitterId() === f.id'>▸</span>
                {{ f.name }} @if (!f.active) { <small class='inactive'>(inactive)</small> }
              </button>
              @if (f.ownHours) { <span class='tag own'>own hours</span> } @else { <span class='follows'>follows Workshop</span> }
              @if (this.mode() === 'week' && this.changedCount(f.days); as n) {
                <span class='tag'>{{ n }} changed this week</span>
              }
              <label class='switch'>
                <input type='checkbox' [checked]='f.ownHours' (change)='this.onOwnHours(f, $any($event.target).checked)' [attr.aria-label]='"Own hours for " + f.name'>
                <span>Own hours</span>
              </label>
            </div>
            @if (f.ownHours && this.openFitterId() === f.id) {
              <ng-container *ngTemplateOutlet='hoursTable; context: { owner: { kind: "fitter", fitter: f } }'></ng-container>
            }
          </div>
        } @empty {
          <p class='hint'>No active fitters. Add them under Workshop › Fitters.</p>
        }
      </section>
    }
  </div>

  <div class='toastMessage' [class.on]='!!this.notice()' role='status'>{{ this.notice() }}</div>
</div>

<!-- A week of hours for one owner (the workshop or a fitter). A day with several shifts prints its name once. -->
<ng-template #hoursTable let-owner='owner'>
  <table class='hoursTable'>
    <thead>
      <tr>
        <th class='cDay'>Day</th>
        @if (this.mode() === 'week') { <th class='cDate'>Date</th> }
        <th class='cTime'>Start</th>
        <th class='cTime'>End</th>
        <th class='cClosed'>Closed</th>
        <th class='cAct'></th>
      </tr>
    </thead>
    <tbody>
      @for (day of this.days(owner); track day.key) {
        @let on = this.presetsOnDay(day);
        @if (day.hours.closed) {
          <tr class='closed' [class.override]='day.overridden'>
            <td class='cDay'>{{ day.label }}</td>
            @if (this.mode() === 'week') { <td class='cDate'>{{ this.dateLabel(day.date) }}</td> }
            <td colspan='2'>
              @if (this.mode() === 'week') {
                <input type='text' class='reason' maxlength='100' placeholder='Reason – e.g. Bank holiday' [value]='day.hours.closedReason ?? ""'
                  (change)='this.onReason(owner, day, $any($event.target).value)' [attr.aria-label]='"Why closed on " + day.label'>
              } @else {
                <span class='closedText'>Closed every week</span>
              }
            </td>
            <td class='cClosed'><input type='checkbox' checked (change)='this.onClosed(owner, day, $any($event.target).checked)' [attr.aria-label]='"Closed on " + day.label'></td>
            <td class='cAct'>
              <div class='actRow'>
                @for (p of this.presets; track p.key) {
                  <button type='button' class='preset' [title]='p.title + " – click to add it to this day"' (click)='this.applyPreset(owner, day, p.key)'>{{ p.label }}</button>
                }
                <button type='button' class='shiftAdd' (click)='this.addShift(owner, day)'>+ shift</button>
                @if (this.mode() === 'week') {
                  <button type='button' class='plain' [disabled]='!day.overridden' (click)='this.reset(owner, day)'
                    [title]='day.overridden ? "Put this day back to the usual week" : "This day already follows the usual week"'>Default</button>
                }
              </div>
            </td>
          </tr>
        } @else {
          @for (s of day.hours.shifts; track $index; let i = $index; let first = $first) {
            <tr [class.override]='day.overridden' [class.extraShift]='!first'>
              <td class='cDay'>{{ first ? day.label : '' }}</td>
              @if (this.mode() === 'week') { <td class='cDate'>{{ first ? this.dateLabel(day.date) : '' }}</td> }
              <td><input type='time' step='300' [value]='this.timeValue(s.startMin)' (change)='this.onTime(owner, day, i, "start", $any($event.target))' [attr.aria-label]='"Start time, " + day.label'></td>
              <td><input type='time' step='300' [value]='this.timeValue(s.endMin)' (change)='this.onTime(owner, day, i, "end", $any($event.target))' [attr.aria-label]='"End time, " + day.label'></td>
              <td class='cClosed'>
                @if (first) { <input type='checkbox' (change)='this.onClosed(owner, day, $any($event.target).checked)' [attr.aria-label]='"Closed on " + day.label'> }
              </td>
              <td class='cAct'>
                <div class='actRow'>
                  @if (first) {
                    @for (p of this.presets; track p.key) {
                      <button type='button' class='preset' [class.on]='on.has(p.key)' [attr.aria-pressed]='on.has(p.key)'
                        [title]='on.has(p.key) ? p.label + " shift is on this day – click to take it off" : p.title + " – click to add it to this day"'
                        (click)='this.applyPreset(owner, day, p.key)'>{{ p.label }}</button>
                    }
                    <button type='button' class='shiftAdd' title='Add another shift to this day' (click)='this.addShift(owner, day)'>+ shift</button>
                  }
                  @if (day.hours.shifts.length > 1) {
                    <button type='button' class='plain' title='Remove this shift, leaving the others' (click)='this.removeShift(owner, day, i)'>Remove</button>
                  }
                  @if (first && this.mode() === 'week') {
                    <button type='button' class='plain' [disabled]='!day.overridden' (click)='this.reset(owner, day)'
                      [title]='day.overridden ? "Put this day back to the usual week" : "This day already follows the usual week"'>Default</button>
                  }
                </div>
              </td>
            </tr>
          }
        }
      }
    </tbody>
  </table>
</ng-template>
